import React, { useEffect } from 'react';
import { createCleanMarkup } from '../../../utils/common';
import { AnchorMenu } from '@cb/apricot-react';

interface DynamicAnchorMenuProps {
  cmsContent?: string | null;
  label?: string;
  id?: string;
}

interface AnchorMenuItem {
  label: string;
  bookmark: string;
}

const parseAnchorItems = (html: string): AnchorMenuItem[] => {
  // Create a temporary DOM element to safely parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Query all h2 elements with id attributes
  const h2Elements = tempDiv.querySelectorAll('h2[id]');

  return Array.from(h2Elements)
    .map((h2) => {
      const id = h2.getAttribute('id');
      const text = h2.textContent?.trim();

      // Only include items with both valid id and text
      if (id && text) {
        return {
          label: text,
          bookmark: id,
        };
      }
      return null;
    })
    .filter((item): item is AnchorMenuItem => item !== null);
};

export default function DynamicAnchorMenu({
  cmsContent = '',
  label = 'On This Page',
  id = 'dynamic-anchor',
}: DynamicAnchorMenuProps) {
  const [anchorMenuItems, setAnchorMenuItems] = React.useState<AnchorMenuItem[]>([]);

  useEffect(() => {
    if (!cmsContent) {
      // eslint-disable-next-line
      setAnchorMenuItems([]);
      return;
    }

    try {
      const html = createCleanMarkup(cmsContent).__html;
      setAnchorMenuItems(parseAnchorItems(html));
    } catch (error) {
      console.error('Error parsing CMS content for anchor menu:', error);
      setAnchorMenuItems([]);
    }
  }, [cmsContent]);

  // Reactively return a menu if and only if we have something to show,
  // otherwise render null.

  if (anchorMenuItems?.length) {
    return (
      <AnchorMenu
        anchorId={id}
        anchorMenuId={`menu-${id}`}
        anchorLabel={label}
        offsetTop={120}
        items={anchorMenuItems}
        close={true}
      />
    );
  }

  return null;
}
